import React from 'react';
import classnames from 'classnames';
import styles from './lab-section.module.scss';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';
import { CreateBox } from 'src/components/admin';
import { LabCategory } from '../../lab-category';
import { Lang } from 'shared/constants/lang';
import { SectionComponent } from 'src/components/common/section/section.component';
import { useSectionOpenState, useViewSelector } from 'src/components/common/view-selector';
import { useSelector } from 'react-redux';
import { haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
import { LabCategoryMovePopup } from '../../lab-category/lab-category-move/lab-category-move';
import { GridItemsNavigator, ListItemsNavigator } from 'src/components/common/items-navigator';

const LabSectionComponent = React.memo(function LabSectionComponent({
  section = {
    id: '',
    title: '',
    fullPath: null,
  },
  children = [],
  adminMode = false,
  activeId = null,
  labSelectValues = () => {},
  deleteCategory = () => {},
  createCategory = () => {},
  updateCategory = () => {},
  editCategory = () => {},
  updateCategorySubcategories = () => {},
  firstItem,
  lastItem = false,
  changeSectionOrder = () => {},
  index = null,
  lang = Lang.ACTION_BUTTON_TOOLTIPS,
}) {
  const { id, title, fullPath, ownerIds, tags, path, type } = section;
  const [isMoveOpen, setMoveOpen] = React.useState(false);
  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));

  // drag and drop items
  const dndGroup = `contentItems-${id}`;
  const [, drop] = useDrop({ accept: dndGroup });
  const [items, setItems] = React.useState(children);
  let isLoading = false;

  React.useEffect(() => {
    setItems(children);
  }, [children]);

  const dragItem = (id, atIndex) => {
    const { item, index } = findItem(id);
    setItems(
      update(items, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = id => {
    const item = items.find(i => `${i.id}` === id);
    return {
      item,
      index: items.indexOf(item),
    };
  };

  const finishDrag = categoryId => {
    updateCategorySubcategories({
      id,
      categoryId,
      index: findItem(categoryId).index,
    });
  };

  // Modification actions
  const deleteCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      // TODO: Analytics
      if (!hasEditPermission) {
        return false;
      }
      deleteCategory({ id, title, path, type, ownerIds });
    },
    [deleteCategory, id, title, path, type, ownerIds, hasEditPermission],
  );

  // Modification actions
  const editCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      // TODO: Analytics
      if (!hasEditPermission) {
        return false;
      }
      editCategory({ id, title, ownerIds, tags, path, type });
    },
    [editCategory, id, ownerIds, title, tags, path, type, hasEditPermission],
  );

  const moveCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();

      if (!hasEditPermission) {
        return;
      }

      setMoveOpen(true);
    },
    [hasEditPermission],
  );

  const createNewCategoryAction = React.useCallback(() => {
    // TODO: Analytics
    createCategory({ path: fullPath, ownerIds, tags });
  }, [createCategory, fullPath, ownerIds, tags]);

  const { isGridView } = useViewSelector();

  const renderItems = () => {
    if (!items) return null;

    const categoryItems = items.map(item => (
      <LabCategory
        key={`content-box-${item.id}`}
        item={item}
        {...{
          adminMode,
          activeId,
          labSelectValues,
          deleteCategory,
          updateCategory,
          editCategory,
          dragItem,
          findItem,
          dndGroup,
          finishDrag,
          lang,
        }}
      />
    ));

    if (isGridView) {
      return (
        <div ref={drop}>
          <GridItemsNavigator className={styles.items} dynamicColumns>
            {categoryItems}
            <CreateBox adminMode={adminMode} onClick={createNewCategoryAction} lang={lang} />
          </GridItemsNavigator>
        </div>
      );
    }

    return (
      <div ref={drop}>
        <ListItemsNavigator className={styles.itemsColumn}>
          {categoryItems}
          <CreateBox adminMode={adminMode} onClick={createNewCategoryAction} lang={lang} />
        </ListItemsNavigator>
      </div>
    );
  };

  const changeOrder = React.useCallback(
    async e => {
      e.stopPropagation();
      const { target } = e;

      if (isLoading) return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isLoading = true;

      const { dataset } = target;

      const toIndex = dataset.direction === 'up' ? index - 1 : index + 1;
      await changeSectionOrder(id, toIndex);
      isLoading = false;
    },
    [index, id],
  );

  const adminActions = [
    {
      label: lang.MOVE_UP,
      onClick: changeOrder,
      props: { 'data-direction': 'up', hidden: firstItem },
    },
    {
      label: lang.MOVE_DOWN,
      onClick: changeOrder,
      props: { 'data-direction': 'down', hidden: lastItem },
    },
    {
      label: lang.EDIT,
      onClick: editCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
    {
      label: lang.MOVE,
      onClick: moveCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
    {
      label: lang.DELETE,
      onClick: deleteCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
  ];

  const onBlur = React.useCallback(
    e => {
      e.stopPropagation();
      const { textContent: value } = e.target;
      // TODO: add analytics

      if (value === title) return false;
      updateCategory({ id, title: value });
    },
    [id, title, updateCategory],
  );

  const { isOpen, onToggle } = useSectionOpenState({
    isFirst: firstItem,
    isContainsActiveBinder: items.some(item => item.id === activeId),
    id,
  });

  return (
    <section
      key={id}
      className={classnames(styles.root, adminMode ? styles.admin : {})}
      role="directory"
      aria-label={title}>
      <span className={styles.anchor} id={id} />
      <SectionComponent
        title={title}
        showActions={adminMode}
        onBlur={onBlur}
        adminActions={adminActions}
        isOpen={isOpen}
        onToggle={onToggle}>
        {renderItems()}
      </SectionComponent>
      <LabCategoryMovePopup
        isOpen={isMoveOpen}
        categoryId={id}
        id={id}
        onFinish={() => setMoveOpen(false)}
      />
    </section>
  );
});

export { LabSectionComponent };
